import React, { useEffect } from "react";
import LiveWaitingIndicator from "./components/shared/LiveWaitingIndicator";
import DashboardOnboardingSteps from "./DashboardOnboardingSteps";
import DashboardOnboardingHeader from "./DashboardOnboardingHeader";
import { ChevronDown } from "lucide-react";
import { useHistory, useLocation } from "react-router-dom";
import { Menu, ButtonVariant, Link, Text, MenuItem } from "@merge-api/merge-javascript-shared";
import DashboardOnboardingNavDropdown from "./DashboardOnboardingNavDropdown";
import { DashboardOnboardingStatus } from "./types";
import { getOnboardingStepCount } from "./utils/onboardingUtils";
import { NAVIGATION_SECTIONS, GET_STARTED_PATHS, ALL_NAV_STEPS } from "./constants";

type Props = {
  dashboardOnboardingStatus: DashboardOnboardingStatus | null;
  children: JSX.Element | JSX.Element[];
};

const DashboardOnboardingWrapper = ({ dashboardOnboardingStatus, children }: Props) => {
  const location = useLocation();
  const history = useHistory();

  const onboardingStepCount = getOnboardingStepCount(dashboardOnboardingStatus);

  // Scroll back to the top of page on step change
  const header = document.getElementById("scroll-to-row");

  useEffect(() => {
    header?.scrollIntoView({ behavior: "smooth" });
  }, [location.pathname]);

  // Find the current step for the mobile nav dropdown display
  const currentStep = ALL_NAV_STEPS.find((step) => step.path === location.pathname);

  return (
    <div className="flex justify-center w-full">
      <div className="w-full xl:max-w-full">
        <div className="flex xl:grid xl:grid-cols-5 gap-x-6 pt-10 min-h-screen">
          {/* Desktop sidebar */}
          <div className="hidden xl:flex flex-col gap-y-2">
            {NAVIGATION_SECTIONS.map((section, sectionIndex) => (
              <React.Fragment key={section.title}>
                <Text
                  variant="pre-title"
                  className={`text-gray-40 mb-2${sectionIndex > 0 ? " mt-2" : ""}`}
                >
                  {section.title}
                </Text>
                {section.steps.map((step) => (
                  <React.Fragment key={step.key}>
                    <DashboardOnboardingSteps
                      stepTitle={step.sidebarTitle}
                      icon={step.icon}
                      lightIndicator={
                        step.statusKey ? (
                          <LiveWaitingIndicator
                            color={dashboardOnboardingStatus?.[step.statusKey] ? "green" : "red"}
                          />
                        ) : undefined
                      }
                      isSelected={location.pathname === step.path}
                      onClick={() => step.navigateFn(history)}
                    />
                    {step.dividerAfter && <div className="h-4" />}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
            <Text variant="pre-title" className="text-gray-40 mt-2">
              Issues? Email <Link href="mailto:support@merge.dev">support@merge.dev</Link>!
            </Text>
          </div>

          {/* Main content */}
          <div className="xl:col-span-4 justify-center self-start">
            {GET_STARTED_PATHS.includes(location.pathname) ? (
              <DashboardOnboardingHeader
                title="Get started"
                badge={`${onboardingStepCount} / 5 complete`}
              />
            ) : (
              <DashboardOnboardingHeader
                title="Advanced"
                subtitle={
                  "Expand your product's integration capabilities with Merge's advanced features"
                }
              />
            )}

            {/* Mobile dropdown navigation */}
            <div className="flex flex-row self-start mb-8 xl:hidden">
              <Menu
                menuPlacement="bottom-end"
                ButtonProps={{
                  variant: ButtonVariant.TertiaryWhite,
                  fullWidth: true,
                  children: (
                    <div className="flex flex-row w-full justify-between items-center">
                      {currentStep && (
                        <DashboardOnboardingNavDropdown
                          toggled={
                            currentStep.statusKey
                              ? dashboardOnboardingStatus?.[currentStep.statusKey]
                              : undefined
                          }
                          icon={currentStep.icon}
                          text={currentStep.label}
                        />
                      )}
                      <ChevronDown size={16} />
                    </div>
                  ),
                }}
              >
                {ALL_NAV_STEPS.map((step) => (
                  <MenuItem key={step.key} onClick={() => step.navigateFn(history)}>
                    {step.label}
                  </MenuItem>
                ))}
              </Menu>
            </div>
            <div className="flex items-center w-full mt-8 mb-16">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOnboardingWrapper;
